"use client";

import { useEffect, useState } from "react";

import { useAuth } from "@/components/auth/auth-provider";
import { isDevEnvironment } from "@/lib/env";

/**
 * Floating admin-only toggle that flips `<html data-env="...">` between dev/prod.
 * Used to preview how env-gated UI will render in the other environment.
 *
 * Affects only CSS gates (`.env-dev-only`, `.env-prod-only`, `.env-hidden`).
 * Does NOT change the JS-side `isDevEnvironment()` value, so JS-driven
 * differences (API key visibility, dev-only fetch paths, etc.) are not
 * previewed — this is a visual-only QA aid.
 *
 * Positioned above the AdminPanel toggle so they don't overlap.
 */
export function EnvPreviewToggle() {
  const { isAdmin } = useAuth();
  const [env, setEnv] = useState<"dev" | "prod">(() => (isDevEnvironment() ? "dev" : "prod"));
  const [isDev, setIsDev] = useState(false);

  useEffect(() => {
    setIsDev(isDevEnvironment());
  }, []);

  // Sync attr on mount + on toggle
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-env", env);
  }, [env]);

  if (!isAdmin || !isDev) return null;

  return (
    <button
      type="button"
      className="env-toggle"
      onClick={() => setEnv((prev) => (prev === "dev" ? "prod" : "dev"))}
      title="Preview the other environment's gated UI"
    >
      {env.toUpperCase()} · click to preview {env === "dev" ? "prod" : "dev"}
    </button>
  );
}
